import pick from 'lodash/pick';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { billingFromStripeAccountType } from '../../util/billing';

// Obtiene la fecha de nacimiento en formato DD/MM/YYYY para Sharetribe
const formatBirthdayForPublicData = dateOfBirth => {
  if (!dateOfBirth) return null;
  if (typeof dateOfBirth === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateOfBirth)) {
    return dateOfBirth;
  }
  if (dateOfBirth?.date instanceof Date) {
    const d = dateOfBirth.date;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }
  return null;
};

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/StripePayoutPage/SET_INITIAL_VALUES';
export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_ERROR';

// ================ Reducer ================ //

const initialState = {
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  fromReturnURL: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const savePayoutDetailsRequest = () => ({
  type: SAVE_PAYOUT_DETAILS_REQUEST,
});
export const savePayoutDetailsError = () => ({
  type: SAVE_PAYOUT_DETAILS_ERROR,
});
export const savePayoutDetailsSuccess = () => ({
  type: SAVE_PAYOUT_DETAILS_SUCCESS,
});

// ================ Thunks ================ //

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      dispatch(savePayoutDetailsSuccess());

      // Guardar DNI y fecha de nacimiento en privateData del usuario en Sharetribe.
      // También al actualizar: antes solo se escribía al crear la cuenta, así que
      // corregir aquí un CIF mal escrito no llegaba nunca a los datos fiscales —
      // el formulario decía que había guardado y el cambio se perdía en silencio.
      {
        const privateData = {};

        const birthday = formatBirthdayForPublicData(values.dateOfBirth);
        if (birthday) {
          privateData.birthday = birthday;
        }

        // El formulario de Stripe ya pregunta particular/empresa con los mismos
        // valores que usamos nosotros, y a la empresa le pedimos además razón
        // social, CIF y el DNI de la persona titular. Son campos planos: el CIF
        // factura a la sociedad y el DNI es el de la persona, que va al aviso al
        // asegurador. Ver src/util/billing.js.
        const currentUser = getState().user?.currentUser;
        Object.assign(privateData, billingFromStripeAccountType(values, currentUser) || {});

        if (Object.keys(privateData).length > 0) {
          dispatch(updateProfile({ privateData }));
        }
      }

      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

export const loadData = () => (dispatch, getState, sdk) => {
  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialValues());

  return dispatch(fetchCurrentUser()).then(response => {
    const currentUser = getState().user.currentUser;
    if (currentUser && currentUser.stripeAccount) {
      dispatch(fetchStripeAccount());
    }
    return response;
  });
};
